import { useState, useEffect, useContext, useRef, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { 
  Send, Smile, Paperclip, Video, VideoOff, ArrowLeft, Mic, MicOff,
  Users, X, Maximize2, Minimize2, Radio, Shield, Trash2, Settings, Palette, Maximize, Minimize, Diamond, Menu
} from "lucide-react";
import { MobileMenu } from "../components/MobileMenu";
import { toast } from "sonner";
import { API, WS_URL, AuthContext, PMContext } from "../App";
import EmojiPicker from "../components/EmojiPicker";
import DraggableResizableWindow from "../components/DraggableResizableWindow";
import UserListPanel from "../components/UserListPanel";
import Avatar from "../components/Avatar";
import Nameplate from "../components/Nameplate";
import WebcamRequestModal from "../components/WebcamRequestModal";
import ImageLightbox from "../components/ImageLightbox";
import AdminPanel from "../components/AdminPanel";
import RoomWebcamGrid from "../components/RoomWebcamGrid";
import DraggableGameSelector from "../components/DraggableGameSelector";
import TriviaPanel from "../components/TriviaPanel";
import SportsTickers from "../components/SportsTickers";
import RacingSponsorsPanel from "../components/RacingSponsorsPanel";
import MusicRadioPanel from "../components/MusicRadioPanel";
import AstrologyPanel from "../components/AstrologyPanel";
import TravelToolsPanel from "../components/TravelToolsPanel";
import MyAccountPanel from "../components/MyAccountPanel";
import PoliticalFightPanel from "../components/PoliticalFightPanel";
import InternetRadio from "../components/InternetRadio";
import NewsTicker from "../components/NewsTicker";
import AskYanaPanel from "../components/AskYanaPanel";
import MovieRoomPanel from "../components/MovieRoomPanel";
import TechTalkPanel from "../components/TechTalkPanel";
import CareerWorkPanel from "../components/CareerWorkPanel";
import FoodCookingPanel from "../components/FoodCookingPanel";
import HealthWellnessPanel from "../components/HealthWellnessPanel";
import PetsAnimalsPanel from "../components/PetsAnimalsPanel";
import SocialMediaPanel from "../components/SocialMediaPanel";
import LocationInfoBar from "../components/LocationInfoBar";
import FightRoomGestures from "../components/FightRoomGestures";
import GenericAskRoomPanel from "../components/GenericAskRoomPanel";
import { playDingDingSound, checkAndPlayMessageSounds } from "../utils/sounds";
import TranslatableMessage from "../components/TranslatableMessage";
import UserActionMenu from "../components/UserActionMenu";
import ShoutboxBar from "../components/ShoutboxBar";
import ReportUserModal from "../components/ReportUserModal";
import { useTranslation } from "../contexts/TranslationContext";

const ChatRoomPage = () => {
  const { roomId } = useParams();
  const navigate = useNavigate();
  const { user, token, logout } = useContext(AuthContext);
  const { openPMWindow } = useContext(PMContext); // Use global PM context
  const { settings: translationSettings } = useTranslation(); // Translation settings
  
  const [messages, setMessages] = useState([]);
  const [users, setUsers] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [typingUsers, setTypingUsers] = useState([]);
  const [showEmoji, setShowEmoji] = useState(false);
  const [roomName, setRoomName] = useState("");
  const [roomFlag, setRoomFlag] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [showWebcam, setShowWebcam] = useState(false);
  const [showAdmin, setShowAdmin] = useState(false);
  const [showMyAccount, setShowMyAccount] = useState(false);
  const [myAccountInitialTab, setMyAccountInitialTab] = useState("overview");
  const [webcamTarget, setWebcamTarget] = useState(null);
  const [isMinimized, setIsMinimized] = useState(false);
  const [webcamRequest, setWebcamRequest] = useState(null);
  const [myWebcamOn, setMyWebcamOn] = useState(false);
  const [lightboxImage, setLightboxImage] = useState(null);
  const [isTestMode, setIsTestMode] = useState(false);  // For camera test/preview
  
  // Age verification modal state
  const [showAgeVerification, setShowAgeVerification] = useState(false);
  const [ageVerificationPending, setAgeVerificationPending] = useState(false);
  const [adultPassPurchasing, setAdultPassPurchasing] = useState(false);
  const [diamondBalance, setDiamondBalance] = useState(0);
  const [ageStatus, setAgeStatus] = useState("none"); // none | pending | approved
  
  // Chat customization state
  const [fontColor, setFontColor] = useState(() => localStorage.getItem('chatFontColor') || '#000000');
  const [bgColor, setBgColor] = useState(() => localStorage.getItem('chatBgColor') || '#FFFFFF');
  const [showSettings, setShowSettings] = useState(false);
  
  // Webcam broadcasting state
  const [isBroadcasting, setIsBroadcasting] = useState(false);
  const [broadcastStream, setBroadcastStream] = useState(null);
  const [webcamViewers, setWebcamViewers] = useState([]);  // List of users viewing my webcam
  const [webcamViewerCounts, setWebcamViewerCounts] = useState({});  // Map of user_id -> viewer count
  const broadcastVideoRef = useRef(null);
  
  // Popped out webcam viewers - max 5
  const [poppedOutWebcams, setPoppedOutWebcams] = useState([]);  // Array of { userId, username, position }
  const MAX_POPPED_WEBCAMS = 5;
  
  // Voice chat state
  const [isVoiceEnabled, setIsVoiceEnabled] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [voiceUsers, setVoiceUsers] = useState([]);
  const [isPushToTalk, setIsPushToTalk] = useState(false);
  const [isPTTActive, setIsPTTActive] = useState(false);
  const localStreamRef = useRef(null);
  const peerConnectionsRef = useRef({}); // Store RTCPeerConnections for each user
  const remoteAudioRef = useRef(null); // For playing remote audio
  const [ignoredIds, setIgnoredIds] = useState([]);
  const ignoredIdsRef = useRef([]);
  const [reportTarget, setReportTarget] = useState(null);
  
  // Fullscreen state
  const [isFullscreen, setIsFullscreen] = useState(false);
  
  // Room banner for monitored rooms
  const [roomBanner, setRoomBanner] = useState(null);

  // Admin-controlled: widget/embedded-app keys hidden in this room
  const [blockedWidgets, setBlockedWidgets] = useState([]);
  const wgtVisible = (key) => !blockedWidgets.includes(key);

  // User-controlled room-view toggles (global, per-user; persisted server-side).
  // A key set to false hides that panel/feature. Default = visible.
  const [viewPrefs, setViewPrefs] = useState({});
  const [showRoomSettings, setShowRoomSettings] = useState(false);
  const prefVisible = (key) => viewPrefs[key] !== false;

  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/users/room-prefs`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => {
        const p = r.data.prefs || {};
        try {
          const lp = JSON.parse(localStorage.getItem("userPreferences") || "{}");
          p.sounds = lp.soundEnabled !== false;
        } catch (_) { p.sounds = true; }
        setViewPrefs(p);
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const saveRoomPref = async (key, value) => {
    const next = { ...viewPrefs, [key]: value };
    setViewPrefs(next);
    if (key === "sounds") {
      try {
        const prefs = JSON.parse(localStorage.getItem("userPreferences") || "{}");
        prefs.soundEnabled = value;
        localStorage.setItem("userPreferences", JSON.stringify(prefs));
      } catch (_) { /* noop */ }
    }
    try {
      await axios.put(`${API}/users/room-prefs`, next, { headers: { Authorization: `Bearer ${token}` } });
    } catch (_) { /* noop */ }
  };

  // ---- Resizable / collapsible left panel ----
  const [leftWidthPct, setLeftWidthPct] = useState(34);
  const [isDesktop, setIsDesktop] = useState(typeof window !== "undefined" ? window.innerWidth >= 768 : true);
  const [isNarrow, setIsNarrow] = useState(typeof window !== "undefined" ? window.innerWidth < 600 : false);
  const [mobileDrawer, setMobileDrawer] = useState(null); // 'webcam' | 'users' | null (mobile side-panel drawers)
  const contentRowRef = useRef(null);
  const draggingSepRef = useRef(false);

  useEffect(() => {
    const onResize = () => {
      const w = window.innerWidth;
      setIsDesktop(w >= 768);
      setIsNarrow(w < 600);
      if (w >= 768) setMobileDrawer(null);
    };
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const startSepDrag = (e) => {
    e.preventDefault();
    draggingSepRef.current = true;
    const onMove = (ev) => {
      if (!draggingSepRef.current || !contentRowRef.current) return;
      const rect = contentRowRef.current.getBoundingClientRect();
      const clientX = ev.touches ? ev.touches[0].clientX : ev.clientX;
      let pct = ((clientX - rect.left) / rect.width) * 100;
      pct = Math.max(18, Math.min(60, pct));
      setLeftWidthPct(pct);
    };
    const onUp = () => {
      draggingSepRef.current = false;
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
      document.removeEventListener("touchmove", onMove);
      document.removeEventListener("touchend", onUp);
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
    document.addEventListener("touchmove", onMove, { passive: false });
    document.addEventListener("touchend", onUp);
  };

  // The left box is "empty" when the user has toggled off all of its widgets.
  const leftPanelActive = prefVisible("webcams")
    || (wgtVisible("games") && prefVisible("games"))
    || (wgtVisible("racing_sponsors") && prefVisible("racing_info"))
    || prefVisible("info_bar");
  
  const wsRef = useRef(null);
  const privateWsRef = useRef(null);  // For receiving incoming PMs
  const reconnectTimerRef = useRef(null);  // Ghost-timer guard: pending WS reconnect setTimeout id
  const isLeavingRef = useRef(false);      // True once we unmount / switch rooms — blocks reconnects
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const fileInputRef = useRef(null);

  // Fullscreen toggle
  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().then(() => {
        setIsFullscreen(true);
      }).catch(err => {
        toast.error("Fullscreen not supported");
      });
    } else {
      document.exitFullscreen().then(() => {
        setIsFullscreen(false);
      });
    }
  };

  // Listen for fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // WebRTC configuration with STUN servers
  const rtcConfig = {
    iceServers: [
      { urls: 'stun:stun.l.google.com:19302' },
      { urls: 'stun:stun1.l.google.com:19302' },
      { urls: 'stun:stun2.l.google.com:19302' }
    ]
  };

  // Help room IDs that need special banner and logging
  const HELP_ROOM_IDS = ['help-desk', 'new-users', 'emergency-911', 'feedback-suggestions'];
  const isHelpRoom = HELP_ROOM_IDS.includes(roomId);
  // Stable per-room hue so each non-geographic room gets its own subtle vibe tint
  const roomHue = (() => { let h = 0; const s = roomId || ''; for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) % 360; return h; })();
  
  // Auto-scroll to bottom when new messages arrive
  const scrollToBottom = useCallback(() => {
    // Scroll the messages container directly — reliable on mobile + desktop, no page jump.
    requestAnimationFrame(() => {
      const c = messagesContainerRef.current;
      if (c) {
        c.scrollTop = c.scrollHeight;
      } else if (messagesEndRef.current) {
        messagesEndRef.current.scrollIntoView();
      }
    });
  }, []);

  // Scroll on new messages
  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  // Fetch initial room data
  useEffect(() => {
    const fetchRoom = async () => {
      try {
        const response = await axios.get(`${API}/rooms/${roomId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setRoomName(response.data.name);
        setMessages(response.data.messages || []);
        setUsers(response.data.users || []);
        setRoomFlag(response.data.flag || null);
        
        // Fetch room banner for monitored rooms
        try {
          const bannerRes = await axios.get(`${API}/room/banner/${roomId}`);
          if (bannerRes.data.banner) {
            setRoomBanner(bannerRes.data.banner);
          }
        } catch (e) {
          // No banner for this room
        }
        
        // Set custom banner for help rooms (will be overwritten by backend banner if available)
        if (HELP_ROOM_IDS.includes(roomId)) {
          // Banner already set by backend call above via /room/banner endpoint
          // Fallback if backend didn't return a banner
          if (!response.data.banner) {
            setRoomBanner("Mr_fuker asks that you keep your questions brief and limited and wait for staff to reply in an IM to discuss your suggestions/feedback/Questions THANK YOU Mr_Fuker");
          }
        }
      } catch (error) {
        console.error("Failed to fetch room:", error);
        toast.error("Failed to load room");
        navigate("/");
      }
    };
    fetchRoom();
  }, [roomId, token, navigate]);

  // Fetch admin-controlled hidden widgets for this room
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await axios.get(`${API}/rooms/${roomId}/widgets`);
        if (active) setBlockedWidgets(res.data?.blocked || []);
      } catch (e) {
        if (active) setBlockedWidgets([]);
      }
    })();
    return () => { active = false; };
  }, [roomId]);

  // WebSocket connection
  useEffect(() => {
    // Fresh mount / room switch — allow reconnects again for THIS room.
    isLeavingRef.current = false;
    const connectWebSocket = () => {
      // Alias / online-light choice made when entering the room (from the Lobby picker)
      let aliasParam = "";
      let showOnlineParam = "";
      try {
        const raw = localStorage.getItem("megachat_room_identity");
        if (raw) {
          const idn = JSON.parse(raw);
          if (idn?.alias) aliasParam = `&alias=${encodeURIComponent(idn.alias)}`;
          if (idn?.show_online === false) showOnlineParam = `&show_online=false`;
        }
      } catch (e) { /* ignore */ }
      const ws = new WebSocket(`${WS_URL}/ws/chat/${roomId}?token=${token}${aliasParam}${showOnlineParam}`);
      
      ws.onopen = () => {
        console.log("Connected to chat room");
        setIsConnected(true);
        
        // Start ping interval to keep connection alive and detect stale connections
        const pingInterval = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: "ping" }));
          }
        }, 30000); // Ping every 30 seconds
        
        // Store ping interval for cleanup
        ws.pingInterval = pingInterval;
      };

      ws.onmessage = (event) => {
        const data = JSON.parse(event.data);
        
        switch (data.type) {
          case "pong":
            // Server responded to our ping - connection is healthy
            break;
          case "movie_state_update":
          case "movie_reaction":
            // Forward to MovieRoomPanel via a window event so we don't have to thread
            // a ref through. The panel only mounts in movie rooms and registers a
            // listener for these.
            window.dispatchEvent(new CustomEvent(data.type === "movie_reaction" ? "movie:reaction" : "movie:state", { detail: data }));
            break;
          case "message":
            setMessages(prev => [...prev, data.message]);
            // Check for sound triggers in message (WTF, middle finger, xoxox)
            if (data.message?.content) {
              checkAndPlayMessageSounds(data.message.content);
            }
            break;
          case "user_joined":
            setUsers(data.users || []);
            setMessages(prev => [...prev, {
              message_id: `system-${Date.now()}`,
              content: `${data.username} has joined the room`,
              message_type: "system",
              timestamp: new Date().toISOString()
            }]);
            break;
          case "user_left":
            setUsers(data.users || []);
            setMessages(prev => [...prev, {
              message_id: `system-${Date.now()}`,
              content: `${data.username} has left the room`,
              message_type: "system",
              timestamp: new Date().toISOString()
            }]);
            break;
          case "system_message":
            // System bot broadcast message
            setMessages(prev => [...prev, {
              message_id: data.message_id || `system-${Date.now()}`,
              content: data.content,
              sender: "System",
              sender_id: "system-bot",
              message_type: "system_broadcast",
              is_system: true,
              timestamp: data.timestamp || new Date().toISOString()
            }]);
            // Play DING DING sound for system messages
            playDingDingSound();
            break;
          case "typing":
            if (data.user_id !== user?.user_id) {
              setTypingUsers(data.typing_users?.filter(u => u !== user?.username) || []);
            }
            break;
          case "voice_joined":
            setVoiceUsers(prev => {
              if (prev.some(u => u.user_id === data.user_id)) return prev;
              return [...prev, { user_id: data.user_id, username: data.username }];
            });
            toast.info(`${data.username} joined voice chat`);
            // If we're in voice chat, initiate connection to new user
            if (isVoiceEnabled && data.user_id !== user?.user_id) {
              createPeerConnection(data.user_id, true);
            }
            break;
          case "voice_banned":
            // Admin has banned this user's IP from voice chat
            setIsVoiceEnabled(false);
            toast.error(
              `🎙️ You have been banned from using voice chat.\n${data.reason ? `Reason: ${data.reason}` : ''}${data.expires_at ? `\nExpires: ${new Date(data.expires_at).toLocaleString()}` : ' (permanent)'}`,
              { duration: 8000 }
            );
            break;
          case "voice_left":
            setVoiceUsers(prev => prev.filter(u => u.user_id !== data.user_id));
            // Close peer connection for this user
            if (peerConnectionsRef.current[data.user_id]) {
              peerConnectionsRef.current[data.user_id].close();
              delete peerConnectionsRef.current[data.user_id];
            }
            break;
          case "voice_offer":
            // Received WebRTC offer from another user
            handleVoiceOffer(data.from_user_id, data.offer);
            break;
          case "voice_answer":
            // Received WebRTC answer
            handleVoiceAnswer(data.from_user_id, data.answer);
            break;
          case "voice_ice_candidate":
            // Received ICE candidate
            handleVoiceIceCandidate(data.from_user_id, data.candidate);
            break;
          case "webcam_offer":
            // Received WebRTC offer for video - open modal to respond
            toast.info(`${data.from_username} is starting a video call`);
            setWebcamTarget({ userId: data.from_user_id, username: data.from_username, incomingOffer: data.offer });
            setShowWebcam(true);
            break;
          case "webcam_signal":
            if (data.signal_type === "offer") {
              toast.info(`${data.from_username} wants to share their webcam`);
              setWebcamTarget({ userId: data.from_user_id, username: data.from_username });
              setShowWebcam(true);
            }
            break;
          case "webcam_request":
            // Someone is requesting to view our webcam
            setWebcamRequest({
              user_id: data.from_user_id,
              username: data.from_username
            });
            break;
          case "webcam_granted":
            // Our request was accepted
            toast.success(`${data.from_username} accepted your webcam request!`);
            setWebcamTarget({ userId: data.from_user_id, username: data.from_username });
            setShowWebcam(true);
            break;
          case "webcam_denied":
            toast.error(`${data.from_username} denied your webcam request`);
            break;
          case "webcam_access_denied":
            toast.error(data.reason || "This webcam is locked.");
            setWebcamTarget((prev) => (prev && prev.userId === data.broadcaster_id ? null : prev));
            break;
          case "webcam_access_revoked":
            toast.error(`${data.broadcaster_username || "The broadcaster"} stopped sharing their cam with you`);
            setWebcamTarget((prev) => (prev && prev.userId === data.broadcaster_id ? null : prev));
            break;
          case "webcam_invited":
            toast.info(`${data.broadcaster_username} invited you to view their webcam`);
            break;
          case "webcam_viewer_joined":
            // Someone started viewing our webcam
            setWebcamViewers(prev => {
              if (!prev.find(v => v.user_id === data.viewer_id)) {
                return [...prev, { user_id: data.viewer_id, username: data.viewer_username }];
              }
              return prev;
            });
            break;
          case "webcam_viewer_left":
            // Someone stopped viewing our webcam
            setWebcamViewers(prev => prev.filter(v => v.user_id !== data.viewer_id));
            break;
          case "webcam_viewer_counts":
            // Update viewer counts for all users
            setWebcamViewerCounts(data.counts || {});
            break;
          case "user_webcam_status":
            // Update user's webcam status in the user list
            setUsers(prev => prev.map(u => 
              u.user_id === data.user_id ? { ...u, webcam_on: data.webcam_on } : u
            ));
            break;
          case "presence_update":
            // Global presence update - user came online or went offline
            if (data.status === "online") {
              // User came online - they might join this room soon
              console.log(`${data.username} is now online`);
            } else {
              // User went offline - remove from user list if they were in this room
              setUsers(prev => prev.filter(u => u.user_id !== data.user_id));
            }
            break;
          case "kicked":
            toast.error(data.reason || "You have been kicked from the chat");
            navigate("/");
            break;
          case "emergency_alert":
            // Emergency room alert for staff
            toast.error(data.message, {
              duration: 10000,
              icon: "🚨"
            });
            break;
          case "messages_cleared":
            // Admin cleared the chat - clear for everyone
            setMessages([{
              message_id: `system-${Date.now()}`,
              content: `Chat cleared by ${data.cleared_by}`,
              message_type: "system",
              timestamp: new Date().toISOString()
            }]);
            toast.info(`Chat cleared by ${data.cleared_by}`);
            break;
          case "trivia_question":
            // Backend-synced trivia question for all users
            setMessages(prev => [...prev, {
              message_id: `trivia-q-${Date.now()}`,
              content: `🧠 TRIVIA: ${data.question.question} [${data.question.category} - ${data.question.points}pts + 3 F-Chips] ⏱️ ${data.time_limit || 30} seconds!`,
              username: "🧠 Trivia Bot",
              sender_id: "trivia-bot",
              is_system: true,
              is_trivia: true,
              timestamp: new Date().toISOString()
            }]);
            break;
          case "trivia_winner":
            // Someone answered correctly
            setMessages(prev => [...prev, {
              message_id: `trivia-w-${Date.now()}`,
              content: `🎉 ${data.winner} GOT IT! Answer: ${data.answer.toUpperCase()} (+${data.points}pts + 3 F-Chips) 🏆`,
              username: "🧠 Trivia Bot",
              sender_id: "trivia-bot",
              is_system: true,
              is_trivia: true,
              timestamp: new Date().toISOString()
            }]);
            break;
          case "trivia_timeout":
            // Time's up
            setMessages(prev => [...prev, {
              message_id: `trivia-t-${Date.now()}`,
              content: `⏰ TIME'S UP! The answer was: ${data.answer.toUpperCase()}`,
              username: "🧠 Trivia Bot",
              sender_id: "trivia-bot",
              is_system: true,
              is_trivia: true,
              timestamp: new Date().toISOString()
            }]);
            break;
          default:
            break;
        }
      };

      ws.onclose = (event) => {
        console.log("Disconnected from chat room", event.code, event.reason);
        setIsConnected(false);
        
        // Clear ping interval on close
        if (ws.pingInterval) {
          clearInterval(ws.pingInterval);
        }
        
        // Handle specific close codes
        if (event.code === 4002) {
          // Room is full
          toast.error("Room is full. Returning to lobby...");
          navigate("/");
          return;
        }
        if (event.code === 4004) {
          // Access denied
          toast.error("You don't have access to this room.");
          navigate("/");
          return;
        }
        if (event.code === 4005 || event.reason === "AGE_VERIFICATION_REQUIRED") {
          // Age verification required for 18+ room
          setShowAgeVerification(true);
          return;
        }
        if (event.code === 4006 || event.reason === "SESSION_EXPIRED") {
          // Session expired - user logged in from another location
          toast.error("You have been logged out because you logged in from another location.", {
            duration: 5000
          });
          logout(false); // Don't call API since session is already invalidated
          return;
        }
        
        // Don't reconnect if user is logging out, or we've left/unmounted this room.
        const isLoggingOut = sessionStorage.getItem('isLoggingOut');
        if (isLoggingOut || isLeavingRef.current) {
          console.log("Skipping WebSocket reconnect - leaving room or logging out");
          return;
        }
        
        // Auto-reconnect for normal disconnects (store the timer id so unmount can kill it).
        reconnectTimerRef.current = setTimeout(connectWebSocket, 3000);
      };

      ws.onerror = (error) => {
        console.error("WebSocket error:", error);
      };

      wsRef.current = ws;
    };

    connectWebSocket();

    // Also connect to private messaging WebSocket to receive incoming PMs
    const connectPrivateWs = () => {
      const privateWs = new WebSocket(`${WS_URL}/ws/private?token=${token}`);
      
      privateWs.onmessage = (event) => {
        const data = JSON.parse(event.data);
        
        if (data.type === "private_message") {
          // Auto-open PM window when receiving a message (using global PM context)
          const senderId = data.message.sender_id;
          const senderUsername = data.message.sender_username;
          openPMWindow(senderId, senderUsername);
        }
      };
      
      privateWs.onclose = (event) => {
        console.log("Private WebSocket disconnected", event.code, event.reason);
        // Handle SESSION_EXPIRED
        if (event.code === 4006 || event.reason === "SESSION_EXPIRED") {
          toast.error("You have been logged out because you logged in from another location.", {
            duration: 5000
          });
          logout(false);
        }
      };
      
      privateWsRef.current = privateWs;
    };
    
    connectPrivateWs();

    // PM notification WebSocket is now handled GLOBALLY by GlobalPMNotifications in App.js
    // (subscribes once across every authenticated route, with 20s heartbeat).

    return () => {
      // Block any pending/future reconnect BEFORE closing the socket, and kill the
      // ghost reconnect timer so the app can't secretly rejoin the room after we leave.
      isLeavingRef.current = true;
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      // Closing the socket triggers the server's WebSocketDisconnect handler, which
      // performs the SINGLE presence update (clears current_room -> In Lobby). We do NOT
      // also POST /rooms/leave here — that would cause a second, redundant DB write.
      if (wsRef.current) {
        wsRef.current.close();
      }
      if (privateWsRef.current) {
        privateWsRef.current.close();
      }
      stopVoice();
    };
  }, [roomId, token, user, navigate]);

  // Listen for live voice-ban notifications (from GlobalPMNotifications) and immediately
  // shut down any active voice session.
  useEffect(() => {
    const onVoiceBanned = () => {
      try { stopVoice(); } catch (_) { /* noop */ }
      setIsVoiceEnabled(false);
    };
    window.addEventListener("voice:banned", onVoiceBanned);
    return () => window.removeEventListener("voice:banned", onVoiceBanned);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Create a peer connection for voice chat with another user
  const createPeerConnection = useCallback(async (targetUserId, isInitiator) => {
    if (!localStreamRef.current) return;
    
    // Close existing connection if any
    if (peerConnectionsRef.current[targetUserId]) {
      peerConnectionsRef.current[targetUserId].close();
    }
    
    const pc = new RTCPeerConnection(rtcConfig);
    peerConnectionsRef.current[targetUserId] = pc;
    
    // Add local audio track to the connection
    localStreamRef.current.getAudioTracks().forEach(track => {
      pc.addTrack(track, localStreamRef.current);
    });
    
    // Handle incoming audio from the remote peer
    pc.ontrack = (event) => {
      console.log("Received remote audio track from", targetUserId);
      // Create an audio element to play remote audio
      const audio = new Audio();
      audio.srcObject = event.streams[0];
      audio.autoplay = true;
      audio.id = `remote-audio-${targetUserId}`;
      audio.muted = ignoredIdsRef.current.includes(targetUserId);
      // Remove existing audio element if any
      const existing = document.getElementById(`remote-audio-${targetUserId}`);
      if (existing) existing.remove();
      document.body.appendChild(audio);
    };
    
    // Handle ICE candidates
    pc.onicecandidate = (event) => {
      if (event.candidate && wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({
          type: "voice_ice_candidate",
          target_user_id: targetUserId,
          candidate: event.candidate
        }));
      }
    };
    
    pc.oniceconnectionstatechange = () => {
      console.log(`ICE connection state with ${targetUserId}:`, pc.iceConnectionState);
      if (pc.iceConnectionState === 'connected') {
        toast.success(`Voice connected with user`);
      }
    };
    
    // If we're the initiator, create and send an offer
    if (isInitiator) {
      try {
        const offer = await pc.createOffer();
        await pc.setLocalDescription(offer);
        
        if (wsRef.current?.readyState === WebSocket.OPEN) {
          wsRef.current.send(JSON.stringify({
            type: "voice_offer",
            target_user_id: targetUserId,
            offer: offer
          }));
        }
      } catch (error) {
        console.error("Failed to create offer:", error);
      }
    }
    
    return pc;
  }, [rtcConfig]);

  // Handle incoming voice offer
  const handleVoiceOffer = useCallback(async (fromUserId, offer) => {
    if (!localStreamRef.current) {
      console.warn("Received voice offer but local stream not ready");
      return;
    }
    
    const pc = await createPeerConnection(fromUserId, false);
    if (!pc) return;
    
    try {
      await pc.setRemoteDescription(new RTCSessionDescription(offer));
      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);
      
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({
          type: "voice_answer",
          target_user_id: fromUserId,
          answer: answer
        }));
      }
    } catch (error) {
      console.error("Failed to handle voice offer:", error);
    }
  }, [createPeerConnection]);

  // Handle incoming voice answer
  const handleVoiceAnswer = useCallback(async (fromUserId, answer) => {
    const pc = peerConnectionsRef.current[fromUserId];
    if (!pc) return;
    
    try {
      await pc.setRemoteDescription(new RTCSessionDescription(answer));
    } catch (error) {
      console.error("Failed to handle voice answer:", error);
    }
  }, []);

  // Handle incoming ICE candidate
  const handleVoiceIceCandidate = useCallback(async (fromUserId, candidate) => {
    const pc = peerConnectionsRef.current[fromUserId];
    if (!pc) return;
    
    try {
      await pc.addIceCandidate(new RTCIceCandidate(candidate));
    } catch (error) {
      console.error("Failed to add ICE candidate:", error);
    }
  }, []);

  // Voice chat functions
  const startVoice = async () => {
    try {
      // This will trigger the browser permission dialog
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      });
      localStreamRef.current = stream;
      setIsVoiceEnabled(true);
      // Chat rooms are Push-to-Talk ONLY (no hands-free). Start muted.
      setIsPushToTalk(true);
      setIsMuted(true);
      const initTrack = stream.getAudioTracks()[0];
      if (initTrack) initTrack.enabled = false;
      
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: "voice_join" }));
      }
      
      // Connect to existing voice users
      voiceUsers.forEach(voiceUser => {
        if (voiceUser.user_id !== user?.user_id) {
          createPeerConnection(voiceUser.user_id, true);
        }
      });
      
      toast.success("Voice chat enabled! You can now hear others.");
    } catch (error) {
      console.error("Failed to access microphone:", error);
      if (error.name === 'NotAllowedError') {
        toast.error("Microphone permission denied. Please allow microphone access in your browser settings.");
      } else if (error.name === 'NotFoundError') {
        toast.error("No microphone found. Please connect a microphone.");
      } else {
        toast.error("Failed to access microphone. Please check permissions.");
      }
    }
  };

  const stopVoice = () => {
    // Close all peer connections
    Object.keys(peerConnectionsRef.current).forEach(peerId => {
      peerConnectionsRef.current[peerId].close();
      // Remove audio elements
      const audioEl = document.getElementById(`remote-audio-${peerId}`);
      if (audioEl) audioEl.remove();
    });
    peerConnectionsRef.current = {};
    
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(track => track.stop());
      localStreamRef.current = null;
    }
    setIsVoiceEnabled(false);
    setIsMuted(false);
    
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: "voice_leave" }));
    }
  };

  const toggleMute = () => {
    if (localStreamRef.current) {
      const audioTrack = localStreamRef.current.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = !audioTrack.enabled;
        setIsMuted(!audioTrack.enabled);
      }
    }
  };

  // Push-to-talk handlers
  const handlePTTPress = useCallback(() => {
    if (isPushToTalk && localStreamRef.current) {
      const audioTrack = localStreamRef.current.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = true;
        setIsPTTActive(true);
      }
    }
  }, [isPushToTalk]);

  const handlePTTRelease = useCallback(() => {
    if (isPushToTalk && localStreamRef.current) {
      const audioTrack = localStreamRef.current.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = false;
        setIsPTTActive(false);
      }
    }
  }, [isPushToTalk]);

  // Enable PTT mode
  const togglePushToTalk = () => {
    if (!isVoiceEnabled) {
      toast.error("Join voice chat first");
      return;
    }
    setIsPushToTalk(!isPushToTalk);
    if (!isPushToTalk) {
      // Turning PTT ON - mute by default
      if (localStreamRef.current) {
        const audioTrack = localStreamRef.current.getAudioTracks()[0];
        if (audioTrack) audioTrack.enabled = false;
      }
      setIsMuted(true);
      toast.info("Push-to-talk enabled. Hold the button to speak.");
    } else {
      // Turning PTT OFF
      toast.info("Push-to-talk disabled");
    }
  };

  // Webcam request handlers
  const handleRequestWebcam = (targetUser) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: "webcam_request",
        target_user_id: targetUser.user_id
      }));
      toast.info(`Webcam request sent to ${targetUser.username}`);
    }
  };

  const handleAcceptWebcamRequest = (fromUser) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: "webcam_grant",
        target_user_id: fromUser.user_id
      }));
      // The grid auto-serves this viewer (starting our cam if needed) on webcam_viewer_joined.
      toast.success(`Approved ${fromUser.username} to view your cam`);
    }
  };

  // Proactively invite a user to view MY cam — grants permission server-side and sends them an IM with a Watch button.
  const handleInviteToCam = (targetUser) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: "webcam_invite",
        target_user_id: targetUser.user_id
      }));
      toast.success(`Invited ${targetUser.username} to view your cam`);
    }
  };

  // Auto-view via IM "Watch" link is handled inside RoomWebcamGrid (pendingWatch prop).

  const handleDenyWebcamRequest = (fromUser) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: "webcam_deny",
        target_user_id: fromUser.user_id
      }));
    }
  };

  // Notify others when webcam is turned on/off
  const broadcastWebcamStatus = useCallback((isOn) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: "webcam_status",
        webcam_on: isOn
      }));
    }
    setMyWebcamOn(isOn);
  }, []);

  // ---- Ignore system ----
  const refreshIgnored = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/users/ignored`, { headers: { Authorization: `Bearer ${token}` } });
      const ids = res.data.ignored_ids || [];
      setIgnoredIds(ids);
      ignoredIdsRef.current = ids;
    } catch (_) { /* noop */ }
  }, [token]);

  useEffect(() => { refreshIgnored(); }, [refreshIgnored]);

  useEffect(() => {
    ignoredIdsRef.current = ignoredIds;
    // Mute ignored users' audio and RESTORE audio for anyone no longer ignored.
    document.querySelectorAll('[id^="remote-audio-"]').forEach((el) => {
      const uid = el.id.replace('remote-audio-', '');
      el.muted = ignoredIds.includes(uid);
    });
  }, [ignoredIds]);

  const handleIgnoreChanged = useCallback((targetUser, nowIgnored) => {
    setIgnoredIds((prev) => {
      const next = nowIgnored
        ? [...new Set([...prev, targetUser.user_id])]
        : prev.filter((x) => x !== targetUser.user_id);
      ignoredIdsRef.current = next;
      return next;
    });
    if (nowIgnored) setPoppedOutWebcams((prev) => prev.filter((w) => w.userId !== targetUser.user_id));
  }, []);

  const sendShoutbox = useCallback((text) => {
    if (!text || !wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;
    wsRef.current.send(JSON.stringify({ type: "message", content: text, message_type: "text" }));
  }, []);

  const sendMessage = () => {
    if (!newMessage.trim() || !wsRef.current || !isConnected) return;    
    if (wsRef.current.readyState !== WebSocket.OPEN) {
      console.warn("WebSocket not ready, message not sent");
      return;
    }

    wsRef.current.send(JSON.stringify({
      type: "message",
      content: newMessage,
      message_type: "text"
    }));

    setNewMessage("");
    setShowEmoji(false);
  };

  const handleTyping = useCallback(() => {
    if (!wsRef.current || !isConnected) return;
    if (wsRef.current.readyState !== WebSocket.OPEN) return;

    wsRef.current.send(JSON.stringify({
      type: "typing",
      is_typing: true
    }));

    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    typingTimeoutRef.current = setTimeout(() => {
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({
          type: "typing",
          is_typing: false
        }));
      }
    }, 2000);
  }, [isConnected]);

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);
    formData.append("authorization", `Bearer ${token}`);

    try {
      const response = await axios.post(`${API}/upload`, formData, {
        headers: { 
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`
        }
      });

      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({
          type: "message",
          content: `Shared a file: ${response.data.file_name}`,
          message_type: "file",
          file_url: response.data.file_url,
          file_name: response.data.file_name
        }));
      }
    } catch (error) {
      toast.error("Failed to upload file");
    }

    e.target.value = "";
  };

  // Webcam broadcasting functions
  const startBroadcast = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { width: { ideal: 640 }, height: { ideal: 480 } },
        audio: false
      });
      setBroadcastStream(stream);
      setIsBroadcasting(true);
      setWebcamViewers([]);
      
      // Notify room that webcam is broadcasting
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({
          type: "webcam_broadcast_start",
          user_id: user?.user_id,
          username: user?.username
        }));
      }
      
      broadcastWebcamStatus(true);
      toast.success("Webcam broadcasting started!");
    } catch (error) {
      console.error("Failed to start broadcast:", error);
      toast.error("Failed to access camera");
    }
  };

  const stopBroadcast = () => {
    if (broadcastStream) {
      broadcastStream.getTracks().forEach(track => track.stop());
      setBroadcastStream(null);
    }
    setIsBroadcasting(false);
    setWebcamViewers([]);
    
    // Notify room that webcam stopped
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: "webcam_broadcast_stop",
        user_id: user?.user_id
      }));
    }
    
    broadcastWebcamStatus(false);
  };

  const removeViewer = (viewerId) => {
    setWebcamViewers(prev => prev.filter(v => v.user_id !== viewerId));
    // Notify the viewer they've been removed
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: "webcam_viewer_removed",
        target_user_id: viewerId
      }));
    }
    toast.info("Viewer removed");
  };

  // Pop-out webcam functions
  const popOutWebcam = (targetUser) => {
    // Check if already popped out
    if (poppedOutWebcams.some(w => w.userId === targetUser.user_id)) {
      toast.info(`${targetUser.username}'s webcam is already open`);
      return;
    }
    
    // Check limit
    if (poppedOutWebcams.length >= MAX_POPPED_WEBCAMS) {
      toast.error(`Maximum ${MAX_POPPED_WEBCAMS} webcam windows allowed. Close one to open another.`);
      return;
    }
    
    // Calculate position (stagger windows)
    const offset = poppedOutWebcams.length * 30;
    const position = { 
      x: 100 + offset, 
      y: 100 + offset 
    };
    
    setPoppedOutWebcams(prev => [...prev, { 
      userId: targetUser.user_id, 
      username: targetUser.username,
      position 
    }]);
    
    toast.success(`Opened ${targetUser.username}'s webcam in new window`);
  };

  const closePopOutWebcam = (userId) => {
    setPoppedOutWebcams(prev => prev.filter(w => w.userId !== userId));
  };

  // Age verification handler — submits a pending request to admin
  const handleAgeVerification = async (confirmed) => {
    if (!confirmed) {
      navigate("/");
      return;
    }
    setAgeVerificationPending(true);
    try {
      const response = await axios.post(`${API}/users/verify-age`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.status === "approved") {
        toast.success("Age verified! Returning to lobby...");
        setShowAgeVerification(false);
        // Use SPA navigation (NOT reload) to keep the session alive.
        // beforeunload would otherwise wipe the sessionStorage token.
        navigate("/");
      } else {
        setAgeStatus("pending");
        toast.success("Request submitted! An admin will review and approve you shortly.");
      }
    } catch (error) {
      console.error("Age verification failed:", error);
      toast.error(error.response?.data?.detail || "Age verification failed. Please try again.");
    } finally {
      setAgeVerificationPending(false);
    }
  };

  // Adult Pass — instant unlock for 200 diamonds
  const handleAdultPassPurchase = async () => {
    setAdultPassPurchasing(true);
    try {
      const response = await axios.post(`${API}/users/adult-pass/purchase`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message || "Adult Pass activated!");
      setShowAgeVerification(false);
      // Use SPA navigation back to lobby; the user can re-enter the now-unlocked room.
      // (window.location.reload would trigger beforeunload and wipe the session.)
      navigate("/");
    } catch (error) {
      console.error("Adult Pass purchase failed:", error);
      toast.error(error.response?.data?.detail || "Purchase failed. Please try again.");
    } finally {
      setAdultPassPurchasing(false);
    }
  };

  // When the age-verification modal opens, fetch fresh balance + status
  useEffect(() => {
    if (!showAgeVerification || !token) return;
    (async () => {
      try {
        const [balRes, statusRes] = await Promise.all([
          axios.get(`${API}/diamonds/balance`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API}/users/age-status`, { headers: { Authorization: `Bearer ${token}` } })
        ]);
        setDiamondBalance(balRes.data.balance || 0);
        setAgeStatus(statusRes.data.status || "none");
      } catch (error) {
        console.error("Failed to fetch age/balance status:", error);
      }
    })();
  }, [showAgeVerification, token]);

  // Send game message to chat (for game wins/announcements)
  const sendGameMessage = (content) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: "chat_message",
        content: content
      }));
    }
  };

  // Save color preferences to localStorage
  const updateFontColor = (color) => {
    setFontColor(color);
    localStorage.setItem('chatFontColor', color);
  };

  const updateBgColor = (color) => {
    setBgColor(color);
    localStorage.setItem('chatBgColor', color);
  };

  // Update broadcast video ref when stream changes
  useEffect(() => {
    if (broadcastStream && broadcastVideoRef.current) {
      broadcastVideoRef.current.srcObject = broadcastStream;
    }
  }, [broadcastStream]);

  // Use global PM context for opening PMs
  const openPM = (targetUser) => {
    if (targetUser.user_id === user?.user_id) return;
    openPMWindow(targetUser.user_id, targetUser.username);
  };

  // Username context menu (triggered by double-click on username in chat)
  const [usernameMenu, setUsernameMenu] = useState(null); // { user, x, y }

  const openUsernameMenu = (e, chatUser) => {
    if (!chatUser || chatUser.user_id === user?.user_id) return;
    e.preventDefault();
    e.stopPropagation();
    setUsernameMenu({
      user: chatUser,
      x: e.clientX,
      y: e.clientY
    });
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // Render message content with custom smileys
  const renderMessageContent = (content, customStyle = {}) => {
    if (!content) return '';
    
    // Pattern to match smiley codes like {bear}, {banana}, etc.
    const smileyPattern = /\{([a-zA-Z0-9_]+)\}/g;
    const parts = [];
    let lastIndex = 0;
    let match;

    while ((match = smileyPattern.exec(content)) !== null) {
      // Add text before the smiley
      if (match.index > lastIndex) {
        parts.push(
          <span key={`text-${lastIndex}`} style={customStyle}>
            {content.substring(lastIndex, match.index)}
          </span>
        );
      }
      
      // Add the smiley image
      const smileyCode = match[0]; // e.g., {bear}
      const smileyName = match[1]; // e.g., bear
      parts.push(
        <img
          key={`smiley-${match.index}`}
          src={`${process.env.REACT_APP_BACKEND_URL}/api/smileys/${smileyName}.gif`}
          alt={smileyCode}
          title={smileyCode}
          className="inline-block h-6 align-middle mx-0.5"
          onError={(e) => {
            // If smiley not found, show the text code instead
            e.target.style.display = 'none';
            e.target.insertAdjacentText('afterend', smileyCode);
          }}
        />
      );
      
      lastIndex = match.index + match[0].length;
    }
    
    // Add remaining text after last smiley
    if (lastIndex < content.length) {
      parts.push(
        <span key={`text-${lastIndex}`} style={customStyle}>
          {content.substring(lastIndex)}
        </span>
      );
    }
    
    return parts.length > 0 ? parts : <span style={customStyle}>{content}</span>;
  };

  // Map user_id -> { avatar, color } from the live user list so we can show avatars/nameplates on messages.
  const userMetaMap = useMemo(() => {
    const m = {};
    (users || []).forEach((u) => { m[u.user_id] = { avatar: u.avatar, color: u.avatar_color }; });
    return m;
  }, [users]);

  const renderMessage = (msg) => {    if (msg.message_type === "system") {
      return (
        <div key={msg.message_id} className="system-message">
          {msg.content}
        </div>
      );
    }
    
    // Trivia Bot message - special highlighted style
    if (msg.is_trivia) {
      const isQuestion = msg.content.includes("TRIVIA:");
      const isWinner = msg.content.includes("GOT IT!");
      const isTimeUp = msg.content.includes("TIME'S UP!");
      
      return (
        <div 
          key={msg.message_id} 
          className={`my-1.5 px-3 py-2 rounded-lg font-arial text-sm ${
            isQuestion 
              ? 'bg-gradient-to-r from-purple-600/90 to-indigo-600/90 text-white border-2 border-purple-400' 
              : isWinner 
                ? 'bg-gradient-to-r from-green-600/90 to-emerald-600/90 text-white border-2 border-green-400'
                : isTimeUp
                  ? 'bg-gradient-to-r from-red-600/90 to-orange-600/90 text-white border-2 border-red-400'
                  : 'bg-purple-100 text-purple-900 border border-purple-300'
          }`}
          data-testid={`trivia-msg-${msg.message_id}`}
        >
          <div className="flex items-center gap-2">
            <span className="font-bold">{msg.username}</span>
            <span className="text-xs opacity-75 ml-auto">[{formatTime(msg.timestamp)}]</span>
          </div>
          <p className="mt-0.5 font-medium">{msg.content.replace('🧠 TRIVIA: ', '').replace('🧠 ', '')}</p>
        </div>
      );
    }
    
    // System broadcast message (from System Bot)
    if (msg.message_type === "system_broadcast" || msg.is_system) {
      return (
        <div key={msg.message_id} className="my-2 p-2 bg-yellow-100 border-2 border-yellow-500 rounded" data-testid={`system-broadcast-${msg.message_id}`}>
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center justify-center w-6 h-6 bg-red-600 text-white text-xs font-bold rounded">
              🤖
            </span>
            <span className="font-bold text-red-600 text-sm">SYSTEM ANNOUNCEMENT</span>
            <span className="text-xs text-gray-500 ml-auto">[{formatTime(msg.timestamp)}]</span>
          </div>
          <p className="mt-1 font-arial text-sm text-gray-800 font-medium">{msg.content}</p>
        </div>
      );
    }

    const isOwn = msg.user_id === user?.user_id;
    const senderMeta = userMetaMap[msg.user_id] || {};

    return (
      <div key={msg.message_id} className="mb-2 text-sm" data-testid={`message-${msg.message_id}`}>
        <span className="chat-timestamp">[{formatTime(msg.timestamp)}]</span>
        <Avatar
          avatar={senderMeta.avatar}
          username={msg.username}
          color={senderMeta.color || "#7B0099"}
          size={18}
          className="inline-block align-middle mx-1"
        />
        <span 
          className={`chat-username ${!isOwn ? 'cursor-pointer' : ''}`}
          onDoubleClick={() => !isOwn && openPM({ user_id: msg.user_id, username: msg.username })}
          onContextMenu={(e) => !isOwn && openUsernameMenu(e, { user_id: msg.user_id, username: msg.username })}
          title={!isOwn ? "Double-click to message · right-click for options" : ""}
          data-testid={`chat-username-${msg.user_id}`}
        >
          {msg.username}
        </span>
        <Nameplate nameplate={senderMeta.avatar?.nameplate} />
        <span className="chat-username">:</span>
        {msg.message_type === "file" ? (
          <span className="ml-1">
            {msg.file_url?.match(/\.(jpg|jpeg|png|gif|webp)$/i) ? (
              <img 
                src={`${process.env.REACT_APP_BACKEND_URL}${msg.file_url}`} 
                alt={msg.file_name} 
                className="chat-image-thumbnail mt-1"
                onClick={() => setLightboxImage({
                  src: `${process.env.REACT_APP_BACKEND_URL}${msg.file_url}`,
                  alt: msg.file_name
                })}
                title="Click to enlarge"
              />
            ) : msg.file_url?.match(/\.(mp4|webm|mov)$/i) ? (
              <video 
                src={`${process.env.REACT_APP_BACKEND_URL}${msg.file_url}`} 
                controls 
                className="file-preview mt-1"
              />
            ) : (
              <a 
                href={`${process.env.REACT_APP_BACKEND_URL}${msg.file_url}`} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-[#0000EE] hover:underline"
              >
                📎 {msg.file_name}
              </a>
            )}
          </span>
        ) : (
          <span className="ml-1 font-arial">
            <TranslatableMessage 
              content={msg.content} 
              renderContent={renderMessageContent}
              customStyle={{ color: isOwn ? fontColor : undefined }}
            />
          </span>
        )}
      </div>
    );
  };

  return (
    <div className="bg-[#C0C0C0] flex flex-col overflow-hidden relative" style={{ height: "var(--app-vh, 100dvh)" }} data-testid="chatroom-root">
      {/* Faded flag background for geographic (World Chat) rooms */}
      {roomFlag && (
        <div
          aria-hidden="true"
          className="fixed inset-0 z-0 pointer-events-none bg-center bg-cover"
          style={{
            backgroundImage: `url(https://flagcdn.com/w1280/${roomFlag}.png)`,
            opacity: 0.14,
            filter: "saturate(1.1)",
          }}
          data-testid="room-flag-bg"
        />
      )}
      {/* Per-room 'vibe' tint so every room feels a little different */}
      {!roomFlag && (
        <div
          aria-hidden="true"
          className="fixed inset-0 z-0 pointer-events-none"
          style={{
            background: `linear-gradient(135deg, hsla(${roomHue}, 70%, 55%, 0.10), hsla(${(roomHue + 60) % 360}, 70%, 45%, 0.10))`,
          }}
          data-testid="room-vibe-bg"
        />
      )}
      {/* Subtle background watermark logo - bottom-left corner so it doesn't interfere with the chat panels */}
      <img
        src="https://customer-assets.emergentagent.com/job_80904b6f-7286-4784-ad3d-292b160c5a80/artifacts/crjane52_MEGA%20CHAT%20LOGO.png"
        alt=""
        aria-hidden="true"
        className="fixed bottom-2 left-2 w-16 h-16 object-contain opacity-30 pointer-events-none select-none z-0"
      />
      {/* Fixed Header */}
      <div className="flex-shrink-0 retro-window mx-2 mt-2" data-testid="chatroom-window">
        {/* Title Bar - Always visible */}
        <div className="retro-title-bar">
          <span className="flex items-center gap-2 min-w-0 flex-1">
            <img 
              src="https://customer-assets.emergentagent.com/job_80904b6f-7286-4784-ad3d-292b160c5a80/artifacts/crjane52_MEGA%20CHAT%20LOGO.png" 
              alt="MEGA Chat"
              className="w-6 h-6 object-contain flex-shrink-0"
            />
            <span className="truncate">{roomName}</span>
          </span>
          <div className="flex gap-1 flex-shrink-0">
            <button 
              className="retro-button px-2 py-0 text-xs"
              onClick={() => setIsMinimized(!isMinimized)}
              title={isMinimized ? "Restore" : "Minimize"}
            >
              {isMinimized ? <Maximize2 className="w-3 h-3" /> : "_"}
            </button>
            <button 
              className="retro-button px-2 py-0 text-xs"
              onClick={toggleFullscreen}
              title={isFullscreen ? "Restore" : "Maximize"}
            >
              {isFullscreen ? "❐" : "□"}
            </button>
            <button 
              className="retro-button px-2 py-0 text-xs"
              onClick={() => navigate("/")}
              data-testid="close-room-btn"
              title="Close"
            >
              ×
            </button>
          </div>
        </div>

        {/* Daily Top News ticker (Yahoo headlines) — sits just below the blue title bar */}
        {prefVisible('news_ticker') && (
          <div className="relative z-20" style={isNarrow ? { zoom: 0.7 } : undefined}>
            <NewsTicker />
          </div>
        )}

        {/* Toolbar - Always visible */}
        <div className="bg-[#C0C0C0] border-b border-[#808080] px-2 py-1 flex flex-wrap items-center gap-2">
          <button 
            className="retro-button flex items-center gap-1 text-xs px-3"
            onClick={() => navigate("/")}
            data-testid="back-to-lobby-btn"
          >
            <ArrowLeft className="w-4 h-4" />
            Lobby
          </button>
          {!isDesktop && (
            <>
              <button
                className="retro-button flex items-center gap-1 text-xs px-2"
                onClick={() => setMobileDrawer((d) => (d === "users" ? null : "users"))}
                data-testid="mobile-users-toggle"
                title="Show users in room"
              >
                <Users className="w-4 h-4" /> Users
              </button>
              <button
                className="retro-button flex items-center gap-1 text-xs px-2"
                onClick={() => setMobileDrawer((d) => (d === "webcam" ? null : "webcam"))}
                data-testid="mobile-cams-toggle"
                title="Show webcams"
              >
                <Video className="w-4 h-4" /> Cams
              </button>
              <MobileMenu
                label="More"
                testid="chat-mobile-menu"
                items={[
                  { label: "Room Settings", icon: <Settings className="w-4 h-4" />, onClick: () => setShowRoomSettings(true), testid: "m-room-settings" },
                  { label: "Colors", icon: <Palette className="w-4 h-4" />, onClick: () => setShowSettings(true), testid: "m-colors" },
                  { label: "Account", icon: <Diamond className="w-4 h-4 text-cyan-600" />, onClick: () => setShowMyAccount(true), testid: "m-chat-account" },
                  { label: isFullscreen ? "Exit Fullscreen" : "Fullscreen", icon: isFullscreen ? <Minimize className="w-4 h-4" /> : <Maximize className="w-4 h-4" />, onClick: toggleFullscreen, testid: "m-fullscreen" },
                  { label: "Admin", icon: <Shield className="w-4 h-4 text-purple-700" />, onClick: () => setShowAdmin(true), hidden: !(user?.is_admin || user?.is_moderator), testid: "m-chat-admin" },
                  { label: "Clear Chat", icon: <Trash2 className="w-4 h-4 text-red-600" />, hidden: !user?.is_admin, testid: "m-clear", onClick: async () => {
                      if (confirm('Clear all messages in this room?')) {
                        try {
                          await axios.delete(`${API}/rooms/${roomId}/messages`, { headers: { Authorization: `Bearer ${token}` } });
                          setMessages([]);
                          toast.success('Messages cleared');
                        } catch (err) { toast.error('Failed to clear messages'); }
                      }
                    } },
                ]}
              />
            </>
          )}
          
          {/* Voice Chat Controls */}
          <div className="flex items-center gap-1 ml-2 px-2 py-1 bg-[#E0E0E0] border border-[#808080]">
            {!isVoiceEnabled ? (
              <button 
                className="retro-button flex items-center gap-1 text-xs px-2 bg-green-100"
                onClick={startVoice}
                data-testid="start-voice-btn"
              >
                <Mic className="w-4 h-4 text-green-600" />
                Join Voice
              </button>
            ) : (
              <>
                <button 
                  className={`retro-button flex items-center gap-1 text-xs px-3 py-2 ${isPTTActive ? 'bg-green-200 border-green-500' : 'bg-yellow-100'}`}
                  onMouseDown={handlePTTPress}
                  onMouseUp={handlePTTRelease}
                  onMouseLeave={handlePTTRelease}
                  onTouchStart={handlePTTPress}
                  onTouchEnd={handlePTTRelease}
                  title="Hold to talk"
                  data-testid="ptt-btn"
                >
                  <Radio className={`w-4 h-4 ${isPTTActive ? 'text-green-600' : 'text-yellow-600'}`} />
                  {isPTTActive ? 'Speaking...' : 'Hold to Talk'}
                </button>
                <span className="font-arial text-[10px] text-[#555] self-center">Push-to-talk only</span>
                <button 
                  className="retro-button flex items-center gap-1 text-xs px-2 bg-red-100"
                  onClick={stopVoice}
                  data-testid="leave-voice-btn"
                >
                  <X className="w-3 h-3" />
                  Leave Voice
                </button>
                {!isMuted && !isPushToTalk && (
                  <div className="voice-indicator ml-1">
                    <div className="voice-bar" />
                    <div className="voice-bar" />
                    <div className="voice-bar" />
                    <div className="voice-bar" />
                  </div>
                )}
                {isPTTActive && (
                  <div className="voice-indicator ml-1">
                    <div className="voice-bar" />
                    <div className="voice-bar" />
                    <div className="voice-bar" />
                    <div className="voice-bar" />
                  </div>
                )}
              </>
            )}
            {voiceUsers.length > 0 && (
              <span className="text-xs text-[#808080] ml-2">
                {voiceUsers.length} in voice
              </span>
            )}
          </div>
          
          {/* Admin / Account / Clear / Colors / Settings / Fullscreen — desktop only; mobile uses the "More" menu above */}
          <div className="hidden md:flex items-center gap-2 flex-wrap">
          {/* Admin Dashboard Button - Only for admins/mods */}
          {(user?.is_admin || user?.is_moderator) && (
            <button 
              className="retro-button flex items-center gap-1 text-xs px-3 bg-[#7B0099] text-white border-[#7B0099]"
              onClick={() => setShowAdmin(true)}
              data-testid="admin-btn-chat"
            >
              <Shield className="w-4 h-4" />
              Admin
            </button>
          )}
          
          {/* My Account Button */}
          <button 
            className="retro-button flex items-center gap-1 text-xs px-2 bg-gradient-to-r from-cyan-100 to-blue-100 border-cyan-500"
            onClick={() => setShowMyAccount(true)}
            data-testid="my-account-btn-chat"
            title="My Account - Diamonds & F-Chips"
          >
            <Diamond className="w-3 h-3 text-cyan-600" />
            Account
          </button>
          
          {/* Clear Messages Button - Only for admins */}
          {user?.is_admin && (
            <button 
              className="retro-button flex items-center gap-1 text-xs px-2 bg-red-100"
              onClick={async () => {
                if (confirm('Clear all messages in this room?')) {
                  try {
                    await axios.delete(`${API}/rooms/${roomId}/messages`, {
                      headers: { Authorization: `Bearer ${token}` }
                    });
                    setMessages([]);
                    toast.success('Messages cleared');
                  } catch (err) {
                    toast.error('Failed to clear messages');
                  }
                }
              }}
              data-testid="clear-messages-btn"
            >
              <Trash2 className="w-3 h-3 text-red-600" />
              Clear
            </button>
          )}
          
          {/* Settings Button */}
          <button 
            className={`retro-button flex items-center gap-1 text-xs px-2 ${showSettings ? 'bg-blue-100' : ''}`}
            onClick={() => setShowSettings(!showSettings)}
            data-testid="settings-btn"
          >
            <Palette className="w-3 h-3" />
            Colors
          </button>

          {/* Room View Settings (gear) */}
          <button 
            className={`retro-button flex items-center gap-1 text-xs px-2 ${showRoomSettings ? 'bg-blue-100' : ''}`}
            onClick={() => setShowRoomSettings(true)}
            data-testid="room-settings-btn"
            title="Room Settings — show/hide panels, sounds & tickers"
          >
            <Settings className="w-3 h-3" />
            Settings
          </button>
          
          {/* Fullscreen Button */}
          <button 
            className="retro-button flex items-center gap-1 text-xs px-2"
            onClick={toggleFullscreen}
            data-testid="fullscreen-btn"
            title={isFullscreen ? "Exit Fullscreen" : "Fullscreen"}
          >
            {isFullscreen ? <Minimize className="w-3 h-3" /> : <Maximize className="w-3 h-3" />}
          </button>
          </div>
          
          <div className="flex-1" />
          <div className={`w-2 h-2 rounded-full ${isConnected ? 'bg-green-500' : 'bg-red-500'}`} />
          <span className="font-arial text-xs">{isConnected ? 'Connected' : 'Disconnected'}</span>
        </div>
        
        {/* Settings Panel */}
        {showSettings && (
          <div className="bg-[#E0E0E0] border-b border-[#808080] px-3 py-2">
            <div className="flex items-center gap-4 flex-wrap">
              <span className="font-arial text-xs font-bold">Chat Colors:</span>
              
              <div className="flex items-center gap-2">
                <label className="font-arial text-xs">Your Font:</label>
                <input
                  type="color"
                  value={fontColor}
                  onChange={(e) => updateFontColor(e.target.value)}
                  className="w-6 h-6 cursor-pointer border border-[#808080]"
                  data-testid="font-color-picker"
                />
              </div>
              
              <div className="flex items-center gap-2">
                <label className="font-arial text-xs">Background:</label>
                <input
                  type="color"
                  value={bgColor}
                  onChange={(e) => updateBgColor(e.target.value)}
                  className="w-6 h-6 cursor-pointer border border-[#808080]"
                  data-testid="bg-color-picker"
                />
              </div>
              
              <button
                className="retro-button text-xs px-2"
                onClick={() => { updateFontColor('#000000'); updateBgColor('#FFFFFF'); }}
              >
                Reset
              </button>
              
              <div className="flex-1" />
              <button
                className="retro-button text-xs px-2"
                onClick={() => setShowSettings(false)}
              >
                <X className="w-3 h-3" />
              </button>
            </div>
            
            {/* Theme Presets */}
            <div className="flex items-center gap-2 mt-2 pt-2 border-t border-[#808080]">
              <span className="font-arial text-xs font-bold">Themes:</span>
              
              <button
                className="retro-button text-xs px-2 py-1 bg-white text-black border-gray-400"
                onClick={() => { updateFontColor('#000000'); updateBgColor('#FFFFFF'); }}
                title="Classic Light"
                data-testid="theme-light"
              >
                ☀️ Light
              </button>
              
              <button
                className="retro-button text-xs px-2 py-1 bg-[#1a1a2e] text-white border-[#1a1a2e]"
                onClick={() => { updateFontColor('#E0E0E0'); updateBgColor('#1a1a2e'); }}
                title="Dark Mode"
                data-testid="theme-dark"
              >
                🌙 Dark
              </button>
              
              <button
                className="retro-button text-xs px-2 py-1 bg-black text-[#00FF00] border-green-500"
                onClick={() => { updateFontColor('#00FF00'); updateBgColor('#0D0D0D'); }}
                title="Matrix Green"
                data-testid="theme-matrix"
              >
                💻 Matrix
              </button>
              
              <button
                className="retro-button text-xs px-2 py-1 bg-[#0a192f] text-[#64ffda] border-[#64ffda]"
                onClick={() => { updateFontColor('#64ffda'); updateBgColor('#0a192f'); }}
                title="Ocean Blue"
                data-testid="theme-ocean"
              >
                🌊 Ocean
              </button>
              
              <button
                className="retro-button text-xs px-2 py-1 bg-[#2d1b4e] text-[#e879f9] border-[#e879f9]"
                onClick={() => { updateFontColor('#e879f9'); updateBgColor('#2d1b4e'); }}
                title="Cyberpunk Purple"
                data-testid="theme-cyber"
              >
                🎮 Cyber
              </button>
              
              <button
                className="retro-button text-xs px-2 py-1 bg-[#fef3c7] text-[#78350f] border-[#d97706]"
                onClick={() => { updateFontColor('#78350f'); updateBgColor('#fef3c7'); }}
                title="Retro Sepia"
                data-testid="theme-sepia"
              >
                📜 Sepia
              </button>
              
              <button
                className="retro-button text-xs px-2 py-1 bg-[#fce7f3] text-[#9d174d] border-[#ec4899]"
                onClick={() => { updateFontColor('#9d174d'); updateBgColor('#fce7f3'); }}
                title="Pink"
                data-testid="theme-pink"
              >
                💖 Pink
              </button>
              
              <button
                className="retro-button text-xs px-2 py-1 bg-[#064e3b] text-[#a7f3d0] border-[#10b981]"
                onClick={() => { updateFontColor('#a7f3d0'); updateBgColor('#064e3b'); }}
                title="Forest Green"
                data-testid="theme-forest"
              >
                🌲 Forest
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Room View Settings Modal */}
      {showRoomSettings && (
        <div
          className="fixed inset-0 z-[9600] bg-black/40 flex items-center justify-center p-4"
          onClick={() => setShowRoomSettings(false)}
          data-testid="room-settings-modal"
        >
          <div className="retro-window w-80 max-w-full" onClick={(e) => e.stopPropagation()}>
            <div className="retro-title-bar">
              <span className="flex items-center gap-1"><Settings className="w-3 h-3" /> Room Settings</span>
              <button className="retro-button px-2 py-0 text-xs" onClick={() => setShowRoomSettings(false)} data-testid="room-settings-close">×</button>
            </div>
            <div className="p-3 bg-[#C0C0C0]">
              <p className="font-arial text-[11px] text-[#555] mb-2">Show or hide features in your chat rooms. Applies to every room.</p>
              <div className="space-y-1.5">
                {[
                  { key: "sounds", label: "🔊 Chat Sounds" },
                  { key: "webcams", label: "📹 Webcams" },
                  { key: "news_ticker", label: "📰 Daily News Ticker" },
                  { key: "sports_ticker", label: "🏈 Sports Ticker" },
                  { key: "internet_radio", label: "📻 Internet Radio" },
                  { key: "games", label: "🎮 Games List" },
                  { key: "user_list", label: "👥 Users List" },
                  { key: "info_bar", label: "🕒 Info Bar (time & weather)" },
                  { key: "racing_info", label: "🏁 Racing Info (Race Monitor)" },
                ].map((row) => (
                  <label
                    key={row.key}
                    className="flex items-center justify-between bg-white border border-[#808080] px-2 py-1.5 cursor-pointer hover:bg-[#f0f0f0]"
                    data-testid={`room-setting-row-${row.key}`}
                  >
                    <span className="font-arial text-xs">{row.label}</span>
                    <input
                      type="checkbox"
                      className="w-4 h-4"
                      checked={prefVisible(row.key)}
                      onChange={(e) => saveRoomPref(row.key, e.target.checked)}
                      data-testid={`room-setting-toggle-${row.key}`}
                    />
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Mobile side-panel drawer backdrop */}
      {!isDesktop && mobileDrawer && (
        <div
          className="fixed inset-0 z-[9996] bg-black/50"
          onClick={() => setMobileDrawer(null)}
          data-testid="mobile-drawer-backdrop"
        />
      )}

      {/* Main Content Area - Flexible height */}
      {!isMinimized && (
        <div ref={contentRowRef} className="flex-1 mx-2 mb-2 flex flex-col md:flex-row overflow-hidden gap-2 min-h-0">
          {/* LEFT PANEL - Webcam Area (1/3 width on desktop, full width on mobile) */}
          <div
            className={
              isDesktop
                ? `w-full flex flex-col retro-window flex-shrink-0 ${!leftPanelActive ? 'hidden' : ''}`
                : (mobileDrawer === 'webcam'
                    ? 'flex flex-col retro-window fixed top-0 left-0 bottom-0 z-[9997] w-[88vw] max-w-sm shadow-2xl'
                    : 'hidden')
            }
            style={isDesktop && leftPanelActive ? { width: `${leftWidthPct}%` } : undefined}
          >
            <div className="retro-title-bar py-1 flex items-center justify-between">
              <span className="text-xs">📹 Webcam</span>
              {!isDesktop && (
                <button
                  className="close-button-visible"
                  onClick={() => setMobileDrawer(null)}
                  data-testid="close-cams-drawer"
                  title="Close"
                >✕</button>
              )}
            </div>
            
            <div className="flex-1 p-2 bg-[#C0C0C0] flex flex-col overflow-hidden">
              {prefVisible('webcams') && (
                <RoomWebcamGrid
                  wsRef={wsRef}
                  userId={user?.user_id}
                  users={users}
                  isConnected={isConnected}
                  pendingWatch={typeof window !== "undefined" ? new URLSearchParams(window.location.search).get("watch") : null}
                />
              )}
              
              {/* Racing Sponsors Panel - Auto Racing & Sprint Car Racing Rooms */}
              {wgtVisible('racing_sponsors') && prefVisible('racing_info') && <RacingSponsorsPanel roomId={roomId} />}
              
              {/* Travel Tools Panel - Only in Travel Room (now in sidebar) */}
              {wgtVisible('travel_tools') && <TravelToolsPanel roomId={roomId} />}
              
              {/* Tech Talk Panel - Only in Tech Talk Room */}
              {wgtVisible('tech_talk') && <TechTalkPanel roomId={roomId} />}
              
              {/* Career & Work Panel - Only in Career Room */}
              {wgtVisible('career_work') && <CareerWorkPanel roomId={roomId} />}
              
              {/* Food & Cooking Panel - Only in Food Room */}
              {wgtVisible('food_cooking') && <FoodCookingPanel roomId={roomId} />}
              
              {/* Health & Wellness Panel - Only in Health Room */}
              {wgtVisible('health_wellness') && <HealthWellnessPanel roomId={roomId} />}
              
              {/* Pets & Animals Panel - Only in Pets Room */}
              {wgtVisible('pets_animals') && <PetsAnimalsPanel roomId={roomId} />}

              {/* Social Media Panels - TikTok, Snapchat, Instagram, Content Creator, Facebook */}
              {wgtVisible('social_media') && (
                <SocialMediaPanel
                  roomId={roomId}
                  onInsertText={(text) => setNewMessage((prev) => (prev ? `${prev} ${text}` : text))}
                />
              )}
              
              {/* Game Selector */}
              {wgtVisible('games') && prefVisible('games') && (
                <div className="mt-2">
                  <DraggableGameSelector 
                    roomId={roomId} 
                    currentUser={user?.username} 
                    sendGameMessage={sendGameMessage}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Draggable separator (desktop only, when the left box has content) */}
          {isDesktop && leftPanelActive && (
            <div
              onMouseDown={startSepDrag}
              onTouchStart={startSepDrag}
              className="hidden md:block w-1.5 cursor-col-resize bg-[#808080] hover:bg-[#000080] flex-shrink-0 rounded"
              title="Drag to resize"
              data-testid="left-panel-separator"
            />
          )}

          {/* RIGHT PANEL - Chat Area (fills remaining width; full width on mobile) */}
          <div className="w-full flex flex-col retro-window relative flex-1 min-h-0 md:min-w-0">
            {/* Sports/Racing Tickers - Only in Sports and Racing rooms */}
            {wgtVisible('sports_tickers') && prefVisible('sports_ticker') && (
              <div style={isNarrow ? { zoom: 0.7 } : undefined}><SportsTickers roomId={roomId} /></div>
            )}
            
            {/* Internet Radio - Music rooms and Staff room */}
            {wgtVisible('internet_radio') && prefVisible('internet_radio') && <InternetRadio roomId={roomId} />}
            
            {/* Astrology Panel - Only in Astrology Room */}
            {wgtVisible('astrology') && <AstrologyPanel roomId={roomId} />}
            
            {/* Generic Ask Rooms - Adult Ask-style rooms */}
            {wgtVisible('generic_ask') && <GenericAskRoomPanel roomId={roomId} />}
            
            {/* Movie Room Panel - Movie Theater and Movies & TV rooms */}
            {wgtVisible('movie_room') && (roomId === 'movie-theater' || roomId === 'movies-tv') && (
              <div className="mb-2">
                <MovieRoomPanel API={API} token={token} roomId={roomId} isAdmin={user?.is_admin} />
              </div>
            )}
            
            {/* Political Fight Panel - Now a collapsible side panel */}
            {wgtVisible('political_fight') && (
              <PoliticalFightPanel 
                roomId={roomId} 
                onInsertEmoji={(emoji) => setNewMessage(prev => prev + emoji)}
              />
            )}
            
            {/* Ask Yana Panel - Only in Ask Yana Room */}
            {wgtVisible('ask_yana') && (
              <AskYanaPanel 
                roomId={roomId}
                wsRef={wsRef}
                currentUser={user?.username}
              />
            )}
            
            {/* Trivia Panel - Only in Trivia Room */}
            <TriviaPanel 
              roomId={roomId} 
              currentUser={user?.username}
              userId={user?.user_id}
              wsRef={wsRef}
              messages={messages}
              onAddTriviaMessage={(msg) => setMessages(prev => [...prev, msg])}
              API={API}
              token={token}
            />
            
            {/* Chat Messages - Scrollable */}
            <div 
              ref={messagesContainerRef}
              className="flex-1 retro-inset p-2 overflow-y-auto retro-scrollbar font-arial"
              style={{ backgroundColor: bgColor }}
              data-testid="messages-container"
            >
              {/* Geographic Location Info Bar — shows local time + temperature
                  for any room mapped in ROOM_GEO_INFO (USA states, Australia,
                  UK, Brazil, Spain, Italy, France, Asia countries, etc.). */}
              {prefVisible('info_bar') && <LocationInfoBar roomId={roomId} />}

              {/* Political Fight Room Warning Banner */}
              {roomId === "political-fight-room" && (
                <div
                  className="bg-gradient-to-r from-red-700 via-yellow-500 to-blue-700 text-white p-3 rounded-lg mb-3 text-sm border-2 border-yellow-300 shadow-lg text-center font-bold"
                  data-testid="political-fight-warning-banner"
                >
                  <div className="text-yellow-200 text-base mb-1">⚠️ DISCLAIMER ⚠️</div>
                  <div>Everything Said or Done in this Room is for Fun &amp; Entertainment Only — Be Adults, Don't Take Anything Serious.</div>
                </div>
              )}
              {/* Health & Wellness — Emergency Banner */}
              {roomId === "health-wellness" && (
                <div
                  className="bg-gradient-to-r from-red-600 via-red-700 to-red-600 text-white p-3 rounded-lg mb-3 border-2 border-yellow-300 shadow-lg text-center font-bold animate-pulse-slow"
                  data-testid="health-emergency-banner"
                >
                  <div className="flex items-center justify-center gap-2 text-base">
                    <span className="text-2xl">🚨</span>
                    <span className="text-yellow-200">IF YOU ARE HAVING A MEDICAL EMERGENCY, CALL <span className="underline text-white">911</span> IMMEDIATELY</span>
                    <span className="text-2xl">🚨</span>
                  </div>
                  <div className="text-[11px] font-normal text-red-100 mt-1">
                    This room is for general wellness discussion only — it is <strong>not</strong> a substitute for professional medical advice, diagnosis, or treatment.
                  </div>
                </div>
              )}
              {/* Room Banner for monitored rooms */}
              {roomBanner && (
                <div className="bg-gradient-to-r from-purple-800 to-purple-900 text-white p-3 rounded-lg mb-3 text-sm whitespace-pre-line border-2 border-yellow-400 shadow-lg">
                  <div className="font-bold text-yellow-300 mb-1">📢 ROOM NOTICE</div>
                  {roomBanner}
                </div>
              )}
              {messages.filter((m) => !m.user_id || !ignoredIds.includes(m.user_id)).map(renderMessage)}
              <div ref={messagesEndRef} />
            </div>

            {/* Typing Indicator */}
            {typingUsers.length > 0 && (
              <div className="flex-shrink-0 px-2 py-1 text-xs text-[#808080] italic bg-[#C0C0C0]">
                {typingUsers.join(", ")} {typingUsers.length === 1 ? "is" : "are"} typing
                <span className="typing-dot">.</span>
                <span className="typing-dot">.</span>
                <span className="typing-dot">.</span>
              </div>
            )}

            {/* Shoutbox preset buttons (chat rooms only, not Guest Chat) */}
            {roomId !== 'guest-chat' && <ShoutboxBar onSend={sendShoutbox} />}

            {/* Input Area - Fixed at bottom */}
            <div className="flex-shrink-0 p-2 bg-[#C0C0C0] border-t border-[#808080]">
              <div className="flex gap-1 mb-1">
                <button 
                  className="retro-button p-1"
                  onClick={() => setShowEmoji(!showEmoji)}
                  data-testid="emoji-btn"
                >
                  <Smile className="w-4 h-4" />
                </button>
                {/* File uploads disabled in public chat rooms - only available in IMs */}
                <button 
                  className="retro-button p-1 bg-gradient-to-r from-gray-200 to-slate-300"
                  onClick={() => setMessages([])}
                  title="Clear Screen"
                  data-testid="clear-screen-btn"
                >
                  🧹
                </button>
                
                {/* Fight Room Gestures Button */}
                <FightRoomGestures 
                  roomId={roomId} 
                  onInsertEmoji={(emoji) => setNewMessage(prev => prev + emoji)}
                />
              </div>

              {showEmoji && (
                <div className="absolute z-50 bottom-20">
                  <EmojiPicker 
                    onSelect={(emoji) => {
                      setNewMessage(prev => prev + emoji);
                      setShowEmoji(false);
                    }}
                    onClose={() => setShowEmoji(false)}
                  />
                </div>
              )}

              <div className="flex gap-1">
                <input
                  type="text"
                  value={newMessage}
                  onChange={(e) => {
                    setNewMessage(e.target.value);
                    handleTyping();
                  }}
                  onKeyPress={(e) => e.key === "Enter" && sendMessage()}
                  className="retro-input flex-1"
                  placeholder="Type your message..."
                  data-testid="message-input"
                />
                <button 
                  className="retro-button px-4"
                  onClick={sendMessage}
                  data-testid="send-btn"
                >
                  <Send className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Status Bar */}
            <div className="flex-shrink-0 bg-[#C0C0C0] border-t border-[#FFFFFF] px-2 py-1 flex justify-between">
              <span className="font-arial text-xs">{users.length} users in room | created by mr_fuker</span>
              <span className="font-arial text-xs">{messages.length} messages</span>
            </div>
          </div>

          {/* User List Panel - fixed sidebar on desktop, slide-in drawer on mobile */}
          {prefVisible('user_list') && (isDesktop || mobileDrawer === 'users') && (
          <div className={isDesktop ? "w-48 ml-1 flex-shrink-0" : "fixed top-0 right-0 bottom-0 z-[9997] w-[82vw] max-w-xs shadow-2xl overflow-y-auto bg-[#C0C0C0]"}>
            {!isDesktop && (
              <button
                className="close-button-visible absolute top-1 right-1 z-10"
                onClick={() => setMobileDrawer(null)}
                data-testid="close-users-drawer"
                title="Close"
              >✕</button>
            )}
            <UserListPanel 
              users={users} 
              onUserClick={openPM}
              currentUserId={user?.user_id}
              roomId={roomId}
              roomName={roomName}
              voiceUsers={voiceUsers}
              onRequestWebcam={handleRequestWebcam}
              onInviteToCam={handleInviteToCam}
              onViewProfile={(targetUser) => navigate(`/profile/${targetUser.user_id}`)}
              webcamViewerCounts={webcamViewerCounts}
              ignoredIds={ignoredIds}
              onIgnoreChanged={handleIgnoreChanged}
              onReport={(tu) => setReportTarget(tu)}
            />
          </div>
          )}
        </div>
      )}

      {/* Private Message Windows are now rendered globally in App.js to persist across navigation */}

      {/* Webcam Request Modal (privacy approve/deny) */}
      {webcamRequest && (
        <WebcamRequestModal
          fromUser={webcamRequest}
          onAccept={handleAcceptWebcamRequest}
          onDeny={handleDenyWebcamRequest}
          onClose={() => setWebcamRequest(null)}
        />
      )}

      {/* Image Lightbox for enlarged view */}
      {lightboxImage && (
        <ImageLightbox
          src={lightboxImage.src}
          alt={lightboxImage.alt}
          onClose={() => setLightboxImage(null)}
        />
      )}

      {/* Admin Panel Modal */}
      {showAdmin && (
        <AdminPanel 
          onClose={() => setShowAdmin(false)} 
          onStartPM={(targetUser) => openPMWindow(targetUser.user_id, targetUser.username)}
        />
      )}

      {/* My Account Panel */}
      {showMyAccount && <MyAccountPanel onClose={() => setShowMyAccount(false)} initialTab={myAccountInitialTab} />}

      {/* Username Context Menu (double-click / right-click on username in chat) */}
      {usernameMenu && (
        <div
          style={{
            position: 'fixed',
            left: Math.min(usernameMenu.x, window.innerWidth - 220),
            top: Math.min(usernameMenu.y, window.innerHeight - 260),
            zIndex: 10000
          }}
          data-testid="username-context-menu"
        >
          <UserActionMenu
            targetUser={usernameMenu.user}
            onClose={() => setUsernameMenu(null)}
            onStartPM={(tu) => { openPM(tu); setUsernameMenu(null); }}
            onViewProfile={(tu) => { navigate(`/profile/${tu.user_id}`); setUsernameMenu(null); }}
            onRequestWebcam={(tu) => { handleRequestWebcam(tu); setUsernameMenu(null); }}
            onInviteToCam={(tu) => { handleInviteToCam(tu); setUsernameMenu(null); }}
            onInviteToRoom={async (tu) => {
              try {
                await axios.post(`${API}/room-invite`, {
                  target_user_id: tu.user_id,
                  room_id: roomId,
                  room_name: roomName || roomId
                }, { headers: { Authorization: `Bearer ${token}` } });
                toast.success(`Invite sent to ${tu.username}!`);
              } catch (error) {
                toast.error(error.response?.data?.detail || "Failed to send invite");
              }
              setUsernameMenu(null);
            }}
            isIgnored={ignoredIds.includes(usernameMenu.user?.user_id)}
            onIgnoreChanged={handleIgnoreChanged}
            onReport={(tu) => { setReportTarget(tu); setUsernameMenu(null); }}
            showInline={true}
          />
        </div>
      )}

      {/* Report User Modal */}
      {reportTarget && (
        <ReportUserModal
          target={reportTarget}
          room={roomName || roomId}
          recentMessages={messages}
          source="chat_room"
          onClose={() => setReportTarget(null)}
        />
      )}

      {/* Age Verification Modal */}
      {showAgeVerification && (
        <DraggableResizableWindow
          title="⚠️ Age Verification Required"
          onClose={() => handleAgeVerification(false)}
          initialSize={{ width: 480, height: 540 }}
          minSize={{ width: 360, height: 400 }}
          initialPosition={{ x: Math.max(40, (window.innerWidth - 480) / 2), y: 60 }}
          zIndex={9999}
          titleBarColor="bg-gradient-to-r from-red-700 to-purple-800"
        >
          <div className="bg-[#C0C0C0] h-full overflow-auto" data-testid="age-verification-modal">
            <div className="p-6 text-center">
              <div className="text-6xl mb-3">🔞</div>
              <h2 className="text-xl font-bold mb-3">Adult Content (18+)</h2>
              <p className="text-sm text-gray-700 mb-5">
                This room contains adult content intended for users 18 years of age or older.
                Pick how you'd like to unlock access:
              </p>

              {ageStatus === "pending" ? (
                <div className="bg-yellow-50 border-2 border-yellow-400 rounded p-4 mb-4 text-left" data-testid="age-pending-state">
                  <p className="font-bold text-yellow-800 mb-1">⏳ Your request is pending admin review.</p>
                  <p className="text-xs text-yellow-700">
                    You'll be notified once approved. In the meantime, you can skip the wait
                    by purchasing an Adult Pass below.
                  </p>
                </div>
              ) : null}

              <div className="flex flex-col gap-3 mb-2">
                {/* OPTION 1: Free admin-approval request */}
                {ageStatus !== "pending" && (
                  <button
                    className="retro-button px-4 py-3 bg-gray-100 text-left flex items-start gap-3 hover:bg-gray-50"
                    onClick={() => handleAgeVerification(true)}
                    disabled={ageVerificationPending || adultPassPurchasing}
                    data-testid="request-approval-btn"
                  >
                    <div className="text-2xl">📝</div>
                    <div className="flex-1">
                      <div className="font-bold text-sm">Request Approval (FREE)</div>
                      <div className="text-xs text-gray-600">
                        An admin will review your account. Wait time varies.
                      </div>
                    </div>
                    {ageVerificationPending && <div className="text-xs animate-pulse">…</div>}
                  </button>
                )}

                {/* OPTION 2: Adult Pass diamond purchase — instant unlock */}
                <button
                  className="retro-button px-4 py-3 bg-gradient-to-r from-purple-100 to-pink-100 text-left flex items-start gap-3 hover:from-purple-200 hover:to-pink-200 border-2 border-[#7B0099]"
                  onClick={handleAdultPassPurchase}
                  disabled={ageVerificationPending || adultPassPurchasing || diamondBalance < 200}
                  data-testid="adult-pass-btn"
                >
                  <div className="text-2xl">⚡</div>
                  <div className="flex-1">
                    <div className="font-bold text-sm flex items-center gap-2">
                      Adult Pass — Skip the Wait
                      <span className="text-[10px] bg-[#7B0099] text-white px-1.5 py-0.5 rounded">INSTANT</span>
                    </div>
                    <div className="text-xs text-gray-700">
                      Unlock all adult rooms immediately for <span className="font-bold">200 💎</span>.
                      &nbsp;Your balance: <span className={`font-bold ${diamondBalance < 200 ? 'text-red-600' : 'text-green-700'}`}>{diamondBalance.toLocaleString()} 💎</span>
                      {diamondBalance < 200 && <span className="text-red-600"> (insufficient)</span>}
                    </div>
                  </div>
                  {adultPassPurchasing && <div className="text-xs animate-pulse">…</div>}
                </button>
              </div>

              <div className="flex justify-end mt-4">
                <button
                  className="retro-button px-4 py-1.5 bg-gray-200 text-xs"
                  onClick={() => handleAgeVerification(false)}
                  disabled={ageVerificationPending || adultPassPurchasing}
                  data-testid="cancel-age-verify-btn"
                >
                  Cancel and leave room
                </button>
              </div>
            </div>
          </div>
        </DraggableResizableWindow>
      )}

      {/* Floating genre music widgets removed — the single Internet Radio player (available
          in every room) is the only radio now. */}
    </div>
  );
};

export default ChatRoomPage;
